import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import AppDrawer from "~/shared/ui/AppDrawer";
import { getPackageMoves, getPackageVacanciesBackoffice } from "~/services/antecipebackoffice/packageMoves";
import { getApiErrorMessage } from "~/utils/apiError";
import { brl, formatDateTimeBR, formatIsoDateBR } from "~/utils/format";
import { STATUS_SHORT_LABEL } from "~/utils/mergeSelection";
import { describeHistory, mergedIntoFromMoves } from "./moveHistory";
import OriginTags from "./OriginTags";

const PREVIEW_VACANCIES = 6;
const EMPTY = { loading: true, error: null, moves: [], vacancies: [], total: 0 };

export default function PackageDetailDrawer({ packageId, row, onClose, onOpenPackage }) {
  const [state, setState] = useState(EMPTY);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!packageId) return undefined;
    let alive = true;
    setState(EMPTY);
    Promise.all([getPackageMoves(packageId), getPackageVacanciesBackoffice(packageId, { limit: PREVIEW_VACANCIES, offset: 0 })])
      .then(([moves, vacancies]) => {
        if (alive) setState({ loading: false, error: null, moves, vacancies: vacancies.data, total: Number(vacancies.total_items) || 0 });
      })
      .catch((e) => {
        if (alive) setState({ ...EMPTY, loading: false, error: getApiErrorMessage(e, "Não foi possível carregar o detalhe do pacote.") });
      });
    return () => {
      alive = false;
    };
  }, [packageId, attempt]);

  const mergedInto = row?.mergedIntoAdvancePaymentId || mergedIntoFromMoves(state.moves, packageId);
  const history = describeHistory(state.moves, packageId);

  return (
    // AppDrawer já sobe para zIndex.modal (acima da AppBar do layout).
    <AppDrawer
      open={Boolean(packageId)}
      onClose={onClose}
      width="sm"
      title={`Pacote #${packageId}`}
      context={row ? `${row.doctor.name} · ${row.company.name}` : undefined}
    >
      {mergedInto && (
        <Alert severity="info" sx={{ mb: 1 }} action={<Button size="small" onClick={() => onOpenPackage(mergedInto)}>Abrir #{mergedInto}</Button>}>
          Este pacote foi <b>agrupado no #{mergedInto}</b>. Os plantões seguiram para lá.
        </Alert>
      )}
      {row && (
        <Box component="dl" sx={{ display: "grid", gridTemplateColumns: "max-content 1fr", columnGap: 2, rowGap: 0.5, m: 0, mb: 2 }}>
          <dt>Médico</dt><dd style={{ margin: 0 }}>{row.doctor.name}</dd>
          <dt>Empresa</dt><dd style={{ margin: 0 }}>{row.company.name}</dd>
          <dt>Status</dt><dd style={{ margin: 0 }}>{mergedInto ? `Agrupado no #${mergedInto}` : STATUS_SHORT_LABEL[row.status] || row.status}</dd>
          <dt>Pagamento do pacote</dt><dd style={{ margin: 0 }}>{row.paymentDate ? formatIsoDateBR(row.paymentDate) : "a definir"}</dd>
          <dt>Plantões</dt><dd style={{ margin: 0 }}>{row.vacanciesQty} · {brl(row.grossTotal)}</dd>
          <dt>Pedido em</dt>
          <dd style={{ margin: 0 }}>
            {formatIsoDateBR(row.requestDate)}
            <Typography variant="caption" display="block" color="text.secondary">Primeiro pedido do médico neste pacote (vale para o prazo)</Typography>
          </dd>
          <dt>Origem</dt><dd style={{ margin: 0 }}><OriginTags row={row} /></dd>
        </Box>
      )}
      <Typography component="h4" variant="subtitle1" sx={{ fontWeight: 600 }}>Histórico de movimentos</Typography>
      {state.loading && <Skeleton variant="rectangular" height={64} sx={{ my: 1 }} />}
      {state.error && (
        <Alert severity="error" sx={{ my: 1 }} action={<Button color="inherit" size="small" onClick={() => setAttempt((a) => a + 1)}>Tentar de novo</Button>}>
          {state.error}
        </Alert>
      )}
      {!state.loading && !state.error && (
        <Box component="ul" sx={{ pl: 2, my: 1 }}>
          {history.map((item) => (
            <li key={item.key}>
              <div>{item.title}</div>
              <Typography variant="caption" color="text.secondary">{item.at} · {item.detail}</Typography>
            </li>
          ))}
          {row?.createdAt && (
            <li>
              <div>Pacote criado</div>
              <Typography variant="caption" color="text.secondary">{formatDateTimeBR(row.createdAt)}</Typography>
            </li>
          )}
          {!history.length && !row?.createdAt && <li>Nenhum movimento registrado.</li>}
        </Box>
      )}
      <Typography component="h4" variant="subtitle1" sx={{ fontWeight: 600, mt: 1 }}>Plantões</Typography>
      {!state.loading && !state.error && (
        <>
          <Box component="table" sx={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead><tr><th align="left">Plantão</th><th align="left">Unidade</th><th align="left">Data</th><th align="right">Bruto</th></tr></thead>
            <tbody>
              {state.vacancies.map((v) => (
                <tr key={v.id}>
                  <td>#{v.id}</td><td>{v.hospital_name}</td><td>{formatIsoDateBR(v.date)}</td><td align="right">{brl(v.gross_value)}</td>
                </tr>
              ))}
            </tbody>
          </Box>
          {state.total > state.vacancies.length && (
            <Typography variant="caption" color="text.secondary">+ {state.total - state.vacancies.length} plantões</Typography>
          )}
        </>
      )}
    </AppDrawer>
  );
}
